import {
  CHINTU_BEHAVIOUR,
  CHINTU_CONFETTI,
  CHINTU_FINALE,
  CHINTU_MESSAGES,
  CHINTU_STORY_CARD,
  IGNORED,
} from "./chintuConfiguration";

/**
 * The engine behind chintuConfiguration.ts — types, the `when` matcher, and
 * the pure functions that turn a bill total plus a set of answers into "which
 * placard" and "does he come".
 *
 * Nothing in here encodes story content or thresholds. If you're changing what
 * Chintu says or when he shows up, you want chintuConfiguration.ts.
 *
 * Note the import direction: the config imports only TYPES from this file
 * (erased at build), this file imports the config's VALUES. No runtime cycle.
 */

// ---------------------------------------------------------------------------
//  Types
// ---------------------------------------------------------------------------

export type MessageId = string;
/** A button id, or the reserved IGNORED. */
export type AnswerId = string;

export type AnswerMap = Partial<Record<MessageId, AnswerId>>;

/**
 * Omit a message entirely = don't care about it. Give an array = any of those.
 * `{}` matches everything, which is how you write a catch-all.
 */
export type Matcher = Partial<Record<MessageId, AnswerId | AnswerId[]>>;

export interface ChintuButton {
  id: AnswerId;
  label: string;
  style?: "ghost" | "accent";
  /** Italic line held for reactionHoldMs before the placard closes itself. */
  reaction?: string;
  /** Presentational only: show a sub-view rather than closing. */
  opens?: "story";
}

export interface PlacardText {
  kicker: string;
  headline: string;
  sub: string;
}

export interface PlacardMotion {
  ropeLen: number;
  swingMs: number;
  deg: number;
  shake: boolean;
}

export interface CopyVariant {
  when: Matcher;
  copy: PlacardText;
}

export interface ChintuMessage {
  id: MessageId;
  /** ₹ threshold on the committed bill. */
  at: number;
  motion: PlacardMotion;
  buttons: ChintuButton[];
  variants: CopyVariant[];
}

export type Outcome = "ARRIVE" | "NO_SHOW" | "NOTHING";

export interface OutcomeRule {
  when: Matcher;
  then: Outcome;
}

export interface NoShowPlacard {
  motion: PlacardMotion;
  buttons: ChintuButton[];
  copy: PlacardText;
}

export interface ChintuFinale {
  at: number;
  rules: OutcomeRule[];
  noShow: NoShowPlacard;
  override?: (answers: AnswerMap) => Outcome | null | undefined;
}

export interface ChintuStoryCardContent {
  name: string;
  role: string;
  meta: string;
  paragraphs: string[];
  footnotes: string[];
  okLabel: string;
}

export interface ChintuConfettiConfig {
  enabled: boolean;
  pieceCount: number;
  /** 0 = all streamers, 1 = all coins. */
  coinRatio: number;
  coinColor: string;
  coinEdgeColor: string;
  ribbonColors: string[];
  burstMs: number;
  /** Supports the `{amount}` placeholder. */
  bannerText: string;
  bannerHoldMs: number;
}

export interface ChintuBehaviour {
  cooldownMs: number;
  reactionHoldMs: number;
  danceSeconds: number;
  /** Brand mark on the placard rope — the placards speak about Chintu, not as him. */
  ropeBadgeSrc: string;
  /** Chintu's own portrait, for the ID card where he's the subject. */
  faceSrc: string;
  storyClosesPlacard: boolean;
  storageVersion: string;
}

/** What's on screen. Messages use their own id; the no-show placard is special. */
export const NO_SHOW_PLACARD_ID = "__noShow__";
/** Latch key for the finale, kept out of the MessageId namespace. */
export const FINALE_BEAT_ID = "__finale__";

export type Beat =
  | { kind: "message"; id: MessageId; message: ChintuMessage }
  | { kind: "finale"; id: typeof FINALE_BEAT_ID };

// ---------------------------------------------------------------------------
//  Matcher
// ---------------------------------------------------------------------------

/**
 * A matcher passes when every message it names has a matching answer.
 *
 * An unanswered message never satisfies a named expectation — including
 * IGNORED, which is only recorded when a placard was actually shown and
 * dismissed. A message that was silently latched (someone joined a table
 * already past it) has no answer at all, so only `{}` will match. That's
 * deliberate: those moments belonged to whoever was already at the table.
 */
export function matches(matcher: Matcher, answers: AnswerMap): boolean {
  for (const [messageId, expected] of Object.entries(matcher)) {
    const actual = answers[messageId];
    if (actual === undefined) return false;
    if (Array.isArray(expected)) {
      if (!expected.includes(actual)) return false;
    } else if (actual !== expected) {
      return false;
    }
  }
  return true;
}

/** First rule whose `when` passes. Order in the config is the priority. */
export function firstMatch<T extends { when: Matcher }>(
  rules: T[],
  answers: AnswerMap
): T | null {
  for (const rule of rules) {
    if (matches(rule.when, answers)) return rule;
  }
  return null;
}

// ---------------------------------------------------------------------------
//  Thresholds
// ---------------------------------------------------------------------------

const allBeats = (): Beat[] => [
  ...CHINTU_MESSAGES.map((message) => ({
    kind: "message" as const,
    id: message.id,
    message,
  })),
  { kind: "finale" as const, id: FINALE_BEAT_ID },
];

const beatThreshold = (beat: Beat): number =>
  beat.kind === "finale" ? CHINTU_FINALE.at : beat.message.at;

/**
 * Highest beat crossed in a single jump, skipping anything already fired.
 * Keyed on the transition rather than the value, so nudging quantities around
 * a threshold can't re-fire it.
 */
export function crossedBeat(
  before: number,
  after: number,
  alreadyFired: string[] = []
): Beat | null {
  const crossed = allBeats()
    .filter((beat) => !alreadyFired.includes(beat.id))
    .filter((beat) => before < beatThreshold(beat) && after >= beatThreshold(beat))
    .sort((a, b) => beatThreshold(a) - beatThreshold(b));
  return crossed.length ? crossed[crossed.length - 1] : null;
}

/** Beat ids at or below `total` — used to latch silently for late joiners. */
export function beatsAtOrBelow(total: number): string[] {
  return allBeats()
    .filter((beat) => total >= beatThreshold(beat))
    .map((beat) => beat.id);
}

// ---------------------------------------------------------------------------
//  Resolution
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
//  Copy tokens
// ---------------------------------------------------------------------------

/**
 * Any copy string — kicker, headline, sub, or the confetti banner — can carry:
 *
 *   {amount}     the live committed bill when the beat fired (e.g. 1,937)
 *   {threshold}  the milestone that was crossed (e.g. 1,500)
 *
 * Hardcoding the number instead means the copy silently lies the moment a
 * threshold moves, which is exactly what happened to "₹1200 crossed" sitting
 * on a message whose `at` was 1500.
 */
export interface CopyTokens {
  amount: number;
  threshold: number;
}

export const KNOWN_TOKENS = ["amount", "threshold"] as const;

/** Indian grouping: 1,937 and 1,00,000 rather than 100,000. */
export function formatRupees(value: number): string {
  return Math.round(value).toLocaleString("en-IN");
}

export function fillTokens(text: string, tokens: CopyTokens): string {
  return text
    .replace(/\{amount\}/g, formatRupees(tokens.amount))
    .replace(/\{threshold\}/g, formatRupees(tokens.threshold));
}

/** The ₹ milestone a placard belongs to. */
export function thresholdFor(placardId: string): number {
  if (placardId === NO_SHOW_PLACARD_ID) return CHINTU_FINALE.at;
  return messageById(placardId)?.at ?? 0;
}

const FALLBACK_COPY: PlacardText = { kicker: "", headline: "", sub: "" };

export function copyFor(message: ChintuMessage, answers: AnswerMap): PlacardText {
  return (
    firstMatch(message.variants, answers)?.copy ??
    message.variants[0]?.copy ??
    FALLBACK_COPY
  );
}

/** The override runs first; returning null falls through to the rule table. */
export function resolveOutcome(answers: AnswerMap): Outcome {
  const forced = CHINTU_FINALE.override?.(answers);
  if (forced) return forced;
  return firstMatch(CHINTU_FINALE.rules, answers)?.then ?? "NOTHING";
}

export function messageById(id: MessageId): ChintuMessage | null {
  return CHINTU_MESSAGES.find((message) => message.id === id) ?? null;
}

export interface ResolvedPlacard {
  copy: PlacardText;
  buttons: ChintuButton[];
  motion: PlacardMotion;
}

/**
 * Everything ChintuPlacard needs, for a message id or the no-show placard.
 * `amount` is the committed bill snapshotted when this beat fired; pass it and
 * the copy's {amount}/{threshold} tokens are resolved here rather than in the
 * component.
 */
export function resolvePlacard(
  placardId: string,
  answers: AnswerMap,
  amount = 0
): ResolvedPlacard | null {
  const tokens: CopyTokens = { amount, threshold: thresholdFor(placardId) };
  const fill = (copy: PlacardText): PlacardText => ({
    kicker: fillTokens(copy.kicker, tokens),
    headline: fillTokens(copy.headline, tokens),
    sub: fillTokens(copy.sub, tokens),
  });

  if (placardId === NO_SHOW_PLACARD_ID) {
    const { copy, buttons, motion } = CHINTU_FINALE.noShow;
    return { copy: fill(copy), buttons, motion };
  }
  const message = messageById(placardId);
  if (!message) return null;
  return {
    copy: fill(copyFor(message, answers)),
    buttons: message.buttons,
    motion: message.motion,
  };
}

export {
  CHINTU_BEHAVIOUR,
  CHINTU_CONFETTI,
  CHINTU_FINALE,
  CHINTU_MESSAGES,
  CHINTU_STORY_CARD,
  IGNORED,
};

// ---------------------------------------------------------------------------
//  Validation
// ---------------------------------------------------------------------------

/**
 * Rules reference messages and buttons by string id, which is what makes the
 * config readable — and also means a typo silently changes behaviour with no
 * error. `"encorage"` would simply never match and Chintu would quietly never
 * arrive. This catches that. Called once from useChintuStory.
 */
export function validateConfiguration(): string[] {
  const problems: string[] = [];
  const messageIds = CHINTU_MESSAGES.map((m) => m.id);

  const duplicates = messageIds.filter((id, i) => messageIds.indexOf(id) !== i);
  if (duplicates.length) problems.push(`duplicate message ids: ${[...new Set(duplicates)].join(", ")}`);

  for (const reserved of [NO_SHOW_PLACARD_ID, FINALE_BEAT_ID]) {
    if (messageIds.includes(reserved)) problems.push(`"${reserved}" is a reserved id`);
  }

  let previous = -Infinity;
  for (const message of CHINTU_MESSAGES) {
    if (message.at <= previous) {
      problems.push(`message "${message.id}" at ₹${message.at} is not above the previous threshold`);
    }
    previous = message.at;

    if (!message.buttons.length) problems.push(`message "${message.id}" has no buttons`);

    const buttonIds = message.buttons.map((b) => b.id);
    if (new Set(buttonIds).size !== buttonIds.length) {
      problems.push(`message "${message.id}" has duplicate button ids`);
    }
    if (buttonIds.includes(IGNORED)) {
      problems.push(`message "${message.id}" uses the reserved button id "${IGNORED}"`);
    }
    for (const button of message.buttons) {
      if (button.opens === "story" && !CHINTU_STORY_CARD.paragraphs.length) {
        problems.push(`button "${button.id}" opens the story card, but CHINTU_STORY_CARD has no paragraphs`);
      }
    }

    if (!message.variants.length) {
      problems.push(`message "${message.id}" has no copy variants`);
    } else if (Object.keys(message.variants[message.variants.length - 1].when).length !== 0) {
      problems.push(`message "${message.id}" variants have no catch-all ({}) last — some answers would render blank`);
    }
    message.variants.forEach((variant, i) =>
      problems.push(...checkMatcher(variant.when, `message "${message.id}" variant ${i}`))
    );
  }

  if (CHINTU_FINALE.at <= previous) {
    problems.push(`finale at ₹${CHINTU_FINALE.at} is not above the last message threshold`);
  }

  if (!CHINTU_FINALE.rules.length) {
    problems.push("finale has no rules");
  } else if (Object.keys(CHINTU_FINALE.rules[CHINTU_FINALE.rules.length - 1].when).length !== 0) {
    problems.push("finale rules have no catch-all ({}) last — some answer combinations would resolve to NOTHING");
  }
  CHINTU_FINALE.rules.forEach((rule, i) =>
    problems.push(...checkMatcher(rule.when, `finale rule ${i}`))
  );

  // A typo'd token ({ammount}) renders literally in the UI rather than
  // erroring, so it's worth catching here alongside the id checks.
  const copyStrings: [string, string][] = [];
  for (const message of CHINTU_MESSAGES) {
    message.variants.forEach((v, i) => {
      const where = `message "${message.id}" variant ${i}`;
      copyStrings.push([where, v.copy.kicker], [where, v.copy.headline], [where, v.copy.sub]);
    });
  }
  copyStrings.push(
    ["finale no-show", CHINTU_FINALE.noShow.copy.kicker],
    ["finale no-show", CHINTU_FINALE.noShow.copy.headline],
    ["finale no-show", CHINTU_FINALE.noShow.copy.sub],
    ["confetti banner", CHINTU_CONFETTI.bannerText]
  );
  for (const [where, text] of copyStrings) {
    for (const match of text.matchAll(/\{(\w+)\}/g)) {
      if (!(KNOWN_TOKENS as readonly string[]).includes(match[1])) {
        problems.push(
          `${where} uses unknown token "{${match[1]}}" (known: ${KNOWN_TOKENS.map((t) => `{${t}}`).join(", ")})`
        );
      }
    }
  }

  return problems;
}

function checkMatcher(matcher: Matcher, where: string): string[] {
  const problems: string[] = [];
  for (const [messageId, expected] of Object.entries(matcher)) {
    const message = messageById(messageId);
    if (!message) {
      problems.push(`${where} references unknown message "${messageId}"`);
      continue;
    }
    const valid = new Set([...message.buttons.map((b) => b.id), IGNORED]);
    for (const answer of Array.isArray(expected) ? expected : [expected]) {
      if (!valid.has(answer)) {
        problems.push(
          `${where} expects "${answer}" for "${messageId}", which has no such button (valid: ${[...valid].join(", ")})`
        );
      }
    }
  }
  return problems;
}
